// =============================================================================
// supplier-history-client.ts — Client API de l'HISTORIQUE FOURNISSEUR, onglet « Mes participations »
// (DBU §3.9, Lot A / S20-REMEDIATION).
// =============================================================================
// Miroir HTTP des endpoints FOURNISSEUR `GET /supplier/history/participations` (tableau) et
// `GET /supplier/history/participations/:campaignId` (vue détail), session opaque sous
// withTenant(supplier_siret). LECTURE PURE : aucune mutation, aucune règle métier ici (autorité = API :
// siret de la session, tri par défaut/statut/agrégation calculés serveur). AUCUN export fournisseur (V1).
// AUCUNE donnée métier en localStorage (RULE_REF_006). Horodatages = ISO 8601 (string), jamais de Date ORM.
//
// PÉRIMÈTRE V1 : SEUL l'onglet 1 « Mes participations » est câblé. Onglets 2 (chat archivé §2.7.4) et 3
// (annonces JeKo §2.7.3) = V1.5 — NON couverts (aucun client, aucun endpoint).

/** Statut final d'une participation (DBU §3.9) — pilote un LIBELLÉ FR, jamais une couleur seule (WCAG). */
export type ParticipationStatutFinalDto = 'Soumis' | 'NonSoumis' | 'Abandonnee';

/** Une ligne du tableau « Mes participations ». Métadonnées NON nominatives (parité DTO API). */
export interface HistoryParticipationDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly campaignState: string;
  readonly statutFinal: ParticipationStatutFinalDto;
  /**
   * « Clients bénéficiaires (sous réserve de diffusion) » — la diffusion (§2.7.3) est V1.5 : `false` en V1
   * (aucun client diffusé). Le front affiche « Sous réserve de diffusion » (jamais une liste inventée).
   */
  readonly clientsDiffuses: boolean;
  /** Début de période (openAt cycle 1, ISO 8601) ou `null` — clé du tri chrono inverse par défaut. */
  readonly periodeStart: string | null;
  /** Fin de période (close_at max, ISO 8601) ou `null`. */
  readonly periodeEnd: string | null;
  /** Cycles ayant fait l'objet d'une saisie (≥ 1 cellule non vide), triés croissant. */
  readonly cyclesSaisis: readonly number[];
  /** Nombre de profils saisis (distincts, union de tous les cycles). */
  readonly profilsSaisis: number;
}

/** Tableau « Mes participations » (tri par défaut = chronologique inverse, autorité API). */
export interface SupplierHistoryDto {
  readonly generatedAt: string;
  readonly participations: readonly HistoryParticipationDto[];
}

/** Un TJM soumis, par profil × niveau (vue détail, LECTURE SEULE). */
export interface HistoryTjmCellDto {
  readonly profileCode: string;
  readonly profileLabel: string | null;
  readonly family: string | null;
  readonly seniorityLevel: string;
  readonly tjmValueEur: number;
}

/** Une compétence spécifique déclarée (vue détail). */
export interface HistoryDeclaredSkillDto {
  readonly profileCode: string;
  readonly skillCode: string;
}

/** Une date clé du cycle (vue détail). `closeAt` ISO 8601 ou `null`. */
export interface HistoryCycleDateDto {
  readonly cycleNumber: number;
  readonly closeAt: string | null;
}

/**
 * Une action sensible du fournisseur SUR la campagne (R15-GOV-07). Enveloppe NON nominative : le type
 * d'événement (l'UI en dérive un libellé lisible) + son horodatage. Parité DTO API.
 */
export interface HistorySensitiveActionDto {
  readonly eventType: string;
  readonly occurredAt: string;
}

/**
 * État du journal des actions sensibles (R15-GOV-07) : `'disponible'` = journal projeté (possiblement vide,
 * état normal) ; `'indisponible'` = repli honnête (seam audit en erreur) — l'UI affiche un encart neutre,
 * jamais un journal fabriqué.
 */
export type HistoryJournalStatutDto = 'disponible' | 'indisponible';

/**
 * Vue détaillée d'une participation (DBU §3.9) — LECTURE SEULE. Inclut le JOURNAL DES ACTIONS SENSIBLES
 * (R15-GOV-07 : saisies/soumissions/signatures campagne-scopées, cloisonné `tenant_id = <siret de session>`).
 */
export interface HistoryDetailDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly campaignState: string;
  readonly statutFinal: ParticipationStatutFinalDto;
  readonly cycleNumber: number;
  readonly launchAt: string | null;
  readonly cycles: readonly HistoryCycleDateDto[];
  readonly tjms: readonly HistoryTjmCellDto[];
  readonly skills: readonly HistoryDeclaredSkillDto[];
  readonly journalStatut: HistoryJournalStatutDto;
  /** Journal des actions sensibles, plus récent d'abord. VIDE si aucune action ou si `indisponible`. */
  readonly journal: readonly HistorySensitiveActionDto[];
}

/** Genre d'erreur applicative de l'historique fournisseur, mappé pour un message UX explicite. */
export type SupplierHistoryApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API historique (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class SupplierHistoryApiError extends Error {
  public readonly kind: SupplierHistoryApiErrorKind;

  public constructor(
    message: string,
    kind: SupplierHistoryApiErrorKind = 'generic',
  ) {
    super(message);
    this.name = 'SupplierHistoryApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client historique FOURNISSEUR — miroir des endpoints Lot A. LECTURE PURE : le siret vient de
 * la session (jamais du body) ; tri par défaut / statut / agrégation sont l'autorité de l'API.
 */
export interface SupplierHistoryClient {
  /** GET tableau « Mes participations » (toutes campagnes du fournisseur en session). */
  listParticipations(): Promise<SupplierHistoryDto>;
  /** GET vue détaillée (LECTURE SEULE) d'une participation par campagne. */
  getDetail(campaignId: string): Promise<HistoryDetailDto>;
}
